import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import ProjectRole from '@projectMembers/models/projectRole';
import Callout from '@ui/components/Callout/Callout';
import { Divider, Group, Button, Stack } from '@mantine/core';

import { validateEmail } from '../../validators/emailValidator';

import User from '../../entities/user';
import ProjectRolePropType from '../../types/projectRole';
import EmailsInput from '../EmailsInput/EmailsInput';

import './InviteMember.scss';
import Workspace from '../../entities/workspace';

class InviteMember extends Component {
	constructor( props ) {
		super( props );

		this.defaultState = {
			currentEmailInput: '',
			role: props.defaultRole.id,
			emails: []
		};

		this.state = { ...this.defaultState };

		this._inputWithSuggestionsRef = createRef();

		this._onCurrentEmailInputChanged = this._changeCurrentEmailInput.bind( this );
		this._onInviteClicked = this._onInviteClicked.bind( this );
		this._onCancelClicked = this._onCancelClicked.bind( this );
	}

	get isFormValid() {
		return validateEmail( this.state.currentEmailInput );
	}

	get hasInvalidEmail() {
		return this.state.emails.some( email => !email.isValid );
	}

	get isOnlyCurrentUserInvited() {
		return this.state.emails.every( email => email.value === this.props.currentUser?.email );
	}

	get formy() {
		const emails = this.state.emails
			.filter( email => email.value !== this.props.currentUser?.email )
			.map( email => email.value );
		return { role: this.state.role, emails };
	}

	get _hasAddedEmail() {
		return this.state.emails.length > 0;
	}

	get _disableInviteButton() {
		return this.props.disabled
			|| this.props.sending
			|| this.hasInvalidEmail
			|| ( !this._hasAddedEmail && this.state.currentEmailInput.length === 0 )
			|| this.isOnlyCurrentUserInvited
			|| this.hasSurpassedPlanLimit
	}

	get hasSurpassedPlanLimit() {
		const { workspace } = this.props;
		if ( !workspace.subscription.hasMembersLimit ) { return false; }

		const { emails } = this.state;
		return ( workspace.membersCount + emails.length ) > workspace.subscription.maxMembers;
	}

	get membersAddOnsLimit() {
		const { workspace } = this.props;
		if ( !workspace.subscription.hasMembersLimit ) { return null; }
		return workspace.subscription.membersAddOnsLimit;
	}

	_onDeleteEmail = ( emailToDelete ) => {
		this.setState(
			prevState => ( {
				...prevState,
				emails: prevState.emails.filter( email => email.value !== emailToDelete )
			} )
		);
	}

	_isValidEvent = ( e ) => {
		const spaceKey = 32;
		const tabKey = 9;
		const enterKey = 13;

		return ( e.keyCode === spaceKey || e.keyCode === tabKey || e.keyCode === enterKey );
	}

	_onKeyDown = ( event ) => {
		if ( !this._isValidEvent( event ) ) return;
		event.preventDefault();
		this._addTextAsEmail();
		if ( this.isFormValid ) this._inputWithSuggestionsRef?.current?.saveSuggestion();
		this._clearEmail();
	}

	_onPaste = ( event ) => {
		event.preventDefault();
		const emailsFromClipboard = this._extractEmailsFromClipboard( event );
		const parsedEmails = emailsFromClipboard.map( this._parseEmail );
		this.setState( prevState => ( {
			emails: [
				...prevState.emails,
				...parsedEmails
			].uniq( email => email.value )
		} ) );
	}

	_parseEmail = email => ( { value: email, isValid: validateEmail( email ) } )

	_placeHolderText = () => {
		if ( this._hasAddedEmail ) return '';
		return 'Email, space or tab separated';
	}

	_extractEmailsFromClipboard( event ) {
		return event.clipboardData.getData( 'text/plain' )
			.split( /, +|,| +|\n/g )
			.map( email => email.trim() )
			.filter( email => email !== '' );
	}

	_isUniqueEmail( emailToCheck ) {
		return !this.state.emails.map( email => email.value ).includes( emailToCheck );
	}

	_clearEmail() {
		this._changeCurrentEmailInput( '' );
	}

	_changeCurrentEmailInput( value ) {
		this.setState( { currentEmailInput: value } );
	}

	_addTextAsEmail( { then } = { then: () => { } } ) {
		const { currentEmailInput } = this.state;

		if ( currentEmailInput.length === 0 ) { then(); return; }
		if ( !this._isUniqueEmail( currentEmailInput ) ) return;
		this.setState( prevState => ( {
			emails: [
				...prevState.emails,
				this._parseEmail( prevState.currentEmailInput )
			]
		} ), then );
	}

	_addEmailsFromGroups = ( emails ) => {
		emails.forEach( ( email ) => {
			if ( !this._isUniqueEmail( email ) ) return;
			this.setState( prevState => ( {
				emails: [
					...prevState.emails,
					this._parseEmail( email )
				]
			} ) );
		} );
	}

	_onInviteClicked() {
		this._addTextAsEmail( {
			then: () => {
				this._clearEmail();
				if ( this.hasInvalidEmail ) return;
				if ( this.isOnlyCurrentUserInvited ) return;
				this.props.onInviteClick( this.formy );
			}
		} );
	}

	_onCancelClicked() {
		this.props.onCancelClick();
	}

	render() {
		const {
			sending,
			workspace,
			disabled,
			showCancelButton,
			projectMemberRoleName,
			showAddGroupButton
		} = this.props;
		const { currentEmailInput, emails } = this.state;
		const {
			_onCurrentEmailInputChanged, _placeHolderText, _onKeyDown, _onPaste
		} = this;

		return (
			<div className="InviteMember">
				<Stack p="24px" gap="15px">
					<EmailsInput
						ref={this._inputWithSuggestionsRef}
						projectMemberRoleName={projectMemberRoleName}
						value={currentEmailInput}
						addedEmails={emails}
						onChange={_onCurrentEmailInputChanged}
						onKeyDown={_onKeyDown}
						placeholder={!disabled ? _placeHolderText() : 'Only admins can invite people'}
						disabled={disabled || sending}
						onDeleteEmail={this._onDeleteEmail}
						onPaste={_onPaste}
						addEmailsFromGroups={this._addEmailsFromGroups}
						name="invite-email"
						autoComplete="invite-email"
						workspace={workspace}
						showAddGroupButton={showAddGroupButton}
						markAllEmailsAsInvalid={this.hasSurpassedPlanLimit}
					/>
					{this.hasSurpassedPlanLimit && (
						<Callout
							type="promotional"
							text={(
								<>
									You{'\''}ve reached the limit of {this.membersAddOnsLimit} extra add-on.
									Upgrade to <strong>Studio Plan</strong> to add more seats.
								</>
							)}
						/>
					)}
				</Stack>
				<Divider color="var(--color-outline-default)" size={1} />
				<Group position="right" p="6px 12px">
					{showCancelButton ? (
						<Button
							variant="default"
							className="cancel-button"
							onClick={this._onCancelClicked}
							styles={{ root: { backgroundColor: 'var(--color-bg-container)', color: 'var(--color-text-primary)', borderColor: 'var(--color-outline-default)' } }}
						>
							Cancel
						</Button>
					) : null }
					<Button
						className="invite-button"
						disabled={this._disableInviteButton}
						onClick={this._onInviteClicked}
					>
						{sending ? 'Inviting...' : 'Send Invite'}
					</Button>
				</Group>
			</div>
		);
	}
}

InviteMember.propTypes = {
	role: ProjectRolePropType,
	defaultRole: ProjectRolePropType,
	sending: PropTypes.bool,
	disabled: PropTypes.bool,
	showCancelButton: PropTypes.bool,
	onInviteClick: PropTypes.func,
	onCancelClick: PropTypes.func,
	workspace: PropTypes.instanceOf( Workspace ).isRequired,
	projectMemberRoleName: PropTypes.string.isRequired,
	showAddGroupButton: PropTypes.bool,
	currentUser: PropTypes.instanceOf( User ).isRequired
};

InviteMember.defaultProps = {
	role: ProjectRole.projectEditor(),
	defaultRole: ProjectRole.projectEditor(),
	sending: false,
	disabled: false,
	showCancelButton: false,
	onInviteClick: () => {},
	onCancelClick: () => {},
	showAddGroupButton: true
};

export default InviteMember;
